import { paths, ProgramName } from '@common/types';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Link,
  Typography,
} from '@mui/material';
import TimerIcon from '@mui/icons-material/Timer';
import dayjs from 'dayjs';
import { ClipboardButton } from 'components/ClipboardButton';
import { EditAdditionalInformation, SummaryNotes } from './components';
import { useSummary } from './hooks';
import { AddTask } from 'components/AddTask';
import { t } from 'i18next';
import { theme } from '@store';
import {
  BatteryFull,
  Battery4Bar,
  Battery2Bar,
  Check,
} from '@mui/icons-material';
import MemoryConnectLogo from './assets/MemoryConnectLogo.svg';
import { QRCode } from 'react-qr-code';
import { AddServicesAndPrograms } from './components/AddServicesAndPrograms';
import { EditProgramDetails } from './components/EditProgramDetails';
import { ProgramIcon } from './util';
import React from 'react';
import { Insurance } from './components/Insurance';
import { statusMapper } from 'util/statusMapper.ts';
// import { PatientGoals } from '@views/pages/Patient/components/Summary/components/PatientGoals';

const getBatteryIcon = (battery_percentage?: number) => {
  if (!battery_percentage) return '';

  if (battery_percentage > 20) {
    return (
      <>
        <BatteryFull color='success' />
        {`${battery_percentage}%`}
      </>
    );
  }
  if (battery_percentage > 10) {
    return (
      <>
        <Battery4Bar color='warning' />
        {`${battery_percentage}%`}
      </>
    );
  }
  return (
    <>
      <Battery2Bar color='error' />
      {`${battery_percentage}%`}
    </>
  );
};

const getDueDateUrgencyColor = (date: string) => {
  const now = dayjs(new Date());
  const dueDate = dayjs(date);

  const difference = dueDate.diff(now, 'day');

  switch (true) {
    case difference <= 0:
      return theme.palette.red.main;
    case difference <= 7:
      return theme.palette.warning.main;
    default:
      return theme.palette.info.main;
  }
};

interface ISummaryProps {
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
  isEditDisabled: boolean;
  tabPanelInstrumentHeight: number;
}

function chunkArray(
  arr: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json']['programs'][0][],
  chunkSize: number,
) {
  const result = [];
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    result.push(chunk);
  }
  return result;
}

export const Summary = ({
  patient,
  isEditDisabled,
  tabPanelInstrumentHeight,
}: ISummaryProps) => {
  const {
    handleOpenTaskDetails,
    isTokenGenerating,
    memoryConnectToken,
    handleGenerateMemoryConnectToken,
    handleCopyToClipboard,
    showMemoryConnect,
    handleNavigateToAllTasks,
    selectedProgram,
    handleOpenProgramView,
    handleCloseSelectedProgramView,
    programColumnsLimit,
  } = useSummary(patient);

  return (
    <Box
      sx={{
        maxHeight: `calc(100vh - ${tabPanelInstrumentHeight + 20}px)`,
        overflowY: 'auto',
      }}
    >
      {/*<PatientGoals*/}
      {/*  patient_id={patient.id}*/}
      {/*  goalsList={patient.goals}*/}
      {/*  isAddGoalDisabled={!patient.can_goal_be_added}*/}
      {/*  disabled={isEditDisabled}*/}
      {/*/>*/}

      {patient && <SummaryNotes patient={patient} disabled={isEditDisabled} />}

      <Box
        sx={{ flexDirection: 'column', marginTop: 3, display: 'flex', gap: 1 }}
      >
        {patient?.open_tasks.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              p: '1rem 1rem',
              bgcolor: 'white',
              border: `1px solid #0000003B`,
            }}
          >
            <Typography variant='body2' sx={{ color: 'text.disabled' }}>
              {t('patient.summary.noOpenTask')}
            </Typography>
            <AddTask
              color='primary'
              variant='outlined'
              size='small'
              sx={{ color: 'primary' }}
              disabled={
                isEditDisabled || patient?.account_status === 'Inactive'
              }
              searchQuery={patient.display_id}
            />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='subtitle2'>
                {t('patient.summary.openTasks', {
                  number: patient?.open_tasks.length || 0,
                })}
              </Typography>

              <Button
                disabled={patient?.open_tasks.length === 0}
                sx={{ whiteSpace: 'nowrap' }}
                onClick={handleNavigateToAllTasks}
              >
                {t('patient.summary.viewAll')}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  patient.open_tasks.length === 1 ? '1fr' : '1fr 1fr',
                gap: '0.5rem',
              }}
            >
              {patient.open_tasks
                .filter((item, idx) => idx < 4)
                .map((task, index) => (
                  <Card
                    key={`${task.id} - ${index}`}
                    sx={{
                      borderRadius: 0,
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleOpenTaskDetails(task.id)}
                    >
                      <CardContent sx={{ p: '1rem' }}>
                        <Box
                          sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              variant='subtitle2'
                              sx={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            >
                              {task.name}
                            </Typography>
                            <Box>
                              <Typography
                                variant='body2'
                                sx={{
                                  color: theme => theme.palette.semiBlack.main,
                                  ml: '0.5rem',
                                }}
                              >
                                {task.program}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant='body2'>
                              {task.future_task
                                ? 'Upcoming'
                                : statusMapper({
                                    status: task.status,
                                    canvas_status: task.canvas_status,
                                  })}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '0.5rem',
                              }}
                            >
                              <Typography
                                variant='body2'
                                sx={{
                                  color: getDueDateUrgencyColor(task.due_date),
                                }}
                              >
                                {dayjs(task.due_date).format('MMM DD, YYYY')}
                              </Typography>
                              <TimerIcon
                                sx={{
                                  color: getDueDateUrgencyColor(task.due_date),
                                  fontSize: '16px',
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
            </Box>
          </>
        )}
      </Box>

      {patient?.programs.filter(program => !!program.status).length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            p: '1rem 1rem',
            bgcolor: 'white',
            border: `1px solid #0000003B`,
            mt: 4,
          }}
        >
          <Typography variant='body2' sx={{ color: 'text.disabled' }}>
            {t('patient.summary.noPrograms')}
          </Typography>
          <AddServicesAndPrograms
            patient={patient}
            disabled={!patient.has_write_permission}
          />
        </Box>
      ) : (
        <Box sx={{ flexDirection: 'column', display: 'flex', gap: 1, mt: 4 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant='subtitle2'>
              {t('patient.summary.programs', {
                number: patient?.programs.filter(o => o.active).length ?? 0,
              })}{' '}
            </Typography>

            <AddServicesAndPrograms
              patient={patient}
              disabled={!patient.has_write_permission}
            />
          </Box>

          {chunkArray(
            patient.programs.filter(program => !!program.status),
            programColumnsLimit,
          ).map((chunk, chunkIdx) => {
            return (
              <Card
                sx={{
                  display: 'flex',
                  p: '1rem',
                  borderRadius: 0,
                }}
                key={chunkIdx}
              >
                {chunk.map((program, idx) => {
                  return (
                    <React.Fragment key={`${program.id} ${idx}`}>
                      <Box
                        key={`${program.id}-${idx}`}
                        sx={{ display: 'flex', alignItems: 'center', flex: 1 }}
                      >
                        <Box
                          sx={{
                            borderRadius: 0,
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: '0.5rem',
                            px: '0.5rem',
                          }}
                        >
                          <ProgramIcon
                            program={program.name as ProgramName}
                            disabled={!program.active}
                          />
                          <Typography
                            variant='subtitle2'
                            onClick={() => handleOpenProgramView(program)}
                            sx={{
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              color: program.active
                                ? theme.palette.primary.main
                                : 'gray',
                              cursor: 'pointer',
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {program.name}
                          </Typography>
                        </Box>
                      </Box>
                      {idx < chunk.length - 1 && (
                        <Divider
                          orientation='vertical'
                          sx={{ height: '48px', mx: '1rem' }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </Card>
            );
          })}
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {t('patient.summary.programsHelpText')}
          </Typography>
          {selectedProgram && (
            <EditProgramDetails
              handleClose={handleCloseSelectedProgramView}
              isOpen={selectedProgram !== null}
              patient_id={patient.id}
              program_id={selectedProgram.id}
              programName={selectedProgram.name}
              disabled={isEditDisabled}
              patient_name={patient.name}
              patient_birthdate={patient.birthdate}
            />
          )}
        </Box>
      )}

      <Insurance patient_id={patient.id} disabled={isEditDisabled} />

      <Box sx={{ pt: '1rem' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: '1rem',
          }}
        >
          <Typography
            variant='subtitle2'
            sx={{
              width: '100%',
            }}
          >
            {t('patient.summary.additionalInformation')}
          </Typography>

          <EditAdditionalInformation
            patient_id={patient.id}
            disabled={isEditDisabled}
          />
        </Box>

        <Card
          sx={{
            width: '100%',
            borderRadius: 0,
          }}
        >
          <CardContent sx={{ p: '1rem' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '1rem',
                  height: '28px',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.canvasId')}
                </Typography>
                <Typography variant='body2' sx={{ color: 'primary.main' }}>
                  {patient.additional_information.canvas_id ? (
                    <Link
                      href={patient.additional_information.canvas_link}
                      target='_blank'
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {patient.additional_information.canvas_id}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '1rem',
                  height: '28px',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.salesforceId')}
                </Typography>
                <Typography variant='body2' sx={{ color: 'primary.main' }}>
                  {patient.additional_information.salesforce_id ? (
                    <Link
                      href={`https://seniordoc.lightning.force.com/lightning/r/Account/${patient.additional_information.salesforce_id}/view`}
                      target='_blank'
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {patient.additional_information.salesforce_id}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: '28px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.emrId')}
                </Typography>
                <Typography variant='body2' sx={{ pl: '1rem', pr: '0.5rem' }}>
                  {patient.additional_information.emr_id || '-'}
                </Typography>
                {patient.additional_information.emr_id && (
                  <ClipboardButton
                    size={16}
                    textToCopy={patient.additional_information.emr_id || '-'}
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: '28px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.billingId')}
                </Typography>
                <Typography variant='body2' sx={{ pl: '1rem', pr: '0.5rem' }}>
                  {patient.additional_information.billing_id || '-'}
                </Typography>
                {patient.additional_information.billing_id && (
                  <ClipboardButton
                    size={16}
                    textToCopy={
                      patient.additional_information.billing_id || '-'
                    }
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  gap: '1rem',
                  height: '28px',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.careContact')}
                </Typography>
                <Typography variant='body2' sx={{ color: 'primary.main' }}>
                  {patient.additional_information.care_connect_id ? (
                    <Link
                      href={`https://seniordoc.lightning.force.com/lightning/r/Care_Connection__c/${patient.additional_information.care_connect_id}/view`}
                      target='_blank'
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                    >
                      {patient.additional_information.care_connect_id}
                    </Link>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: '28px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  {t('patient.summary.cmsIsoId')}
                </Typography>
                <Typography variant='body2' sx={{ pl: '1rem', pr: '0.5rem' }}>
                  {patient.additional_information.cms_iso_id || '-'}
                </Typography>
                {patient.additional_information.cms_iso_id && (
                  <ClipboardButton
                    size={16}
                    textToCopy={
                      patient.additional_information.cms_iso_id || '-'
                    }
                  />
                )}
              </Box>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  height: '28px',
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ color: theme => theme.palette.semiBlack.main }}
                >
                  Customer ID:
                </Typography>
                <Typography variant='body2' sx={{ pl: '1rem', pr: '0.5rem' }}>
                  {patient.additional_information.customer_id || '-'}
                </Typography>
                {patient.additional_information.customer_id && (
                  <ClipboardButton
                    size={16}
                    textToCopy={
                      patient.additional_information.customer_id || '-'
                    }
                  />
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
        {showMemoryConnect && (
          <Card sx={{ my: 3, borderRadius: 0 }}>
            <CardContent sx={{ p: '16px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box component='img' alt='logo' src={MemoryConnectLogo} />
                  <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                    {patient.memory_connect.tablet_authenticated
                      ? ''
                      : t('patient.summary.notAuthenticated')}
                  </Typography>
                </Box>
                {patient.memory_connect.tablet_authenticated ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getBatteryIcon(patient.memory_connect.battery_percentage)}
                    <Button
                      startIcon={<Check />}
                      variant='outlined'
                      color='success'
                      sx={{ ml: 3 }}
                    >
                      {t('patient.summary.authenticated')}
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant='contained'
                    sx={{ color: 'white' }}
                    loading={isTokenGenerating}
                    onClick={handleGenerateMemoryConnectToken}
                  >
                    {t('patient.summary.generateToken')}
                  </Button>
                )}
              </Box>
              {(patient.memory_connect.tablet_authenticated ||
                !!memoryConnectToken) && (
                <>
                  <Divider
                    sx={{
                      my: 2,
                      width: '100%',
                      height: 1,
                      bgcolor: theme => theme.palette.grey[300],
                    }}
                  />
                  {patient.memory_connect.tablet_authenticated ? (
                    <Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.disabled' }}
                        >
                          {t('patient.summary.latestTokenDate')}
                        </Typography>
                        <Typography variant='body2'>
                          {dayjs(patient.memory_connect.token_date).format(
                            'MMM DD, YYYY - hh:mm A',
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography
                          variant='body2'
                          sx={{ color: 'text.disabled' }}
                        >
                          {t('patient.summary.lastActivity')}
                        </Typography>
                        <Typography variant='body2'>
                          {dayjs(patient.memory_connect.last_active).format(
                            'MMM DD, YYYY - hh:mm A',
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography variant='body2'>
                          {t('patient.summary.tokenText')}
                        </Typography>
                        <Typography
                          variant='caption'
                          sx={{ color: 'text.disabled' }}
                        >
                          {t('patient.summary.scanText')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Typography
                            variant='caption'
                            sx={{
                              p: '0 16px',
                              bgcolor: theme => theme.palette.grey[100],
                              m: 0,
                              height: 36,
                              display: 'flex',
                              alignItems: 'center',
                              color: 'text.disabled',
                            }}
                          >
                            {memoryConnectToken}
                          </Typography>
                          <Button
                            variant='outlined'
                            onClick={() =>
                              handleCopyToClipboard(memoryConnectToken || '')
                            }
                          >
                            {t('patient.summary.copyToken')}
                          </Button>
                        </Box>
                      </Box>
                      <QRCode
                        style={{ maxHeight: 100, maxWidth: 100 }}
                        value={memoryConnectToken || ''}
                      />
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};
